import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { tint } from "../../program-landing/components/program-parts";
import { ProgramIcon } from "../../programs/components/program-icon";
import { useIconLibrary } from "../hooks/use-icon-library";
import { findIcons } from "../icon-library";

import type { FC } from "react";

/** An icon of the library, with its SVG from public.icon. */
export interface LibraryIcon {
  name: string;
  label: string;
  svg: string;
}

const countText = (count: number, search: boolean): string => {
  if (!search) return `${count} ${count === 1 ? "icon" : "icons"}`;
  return `${count} ${count === 1 ? "icon matches" : "icons match"}`;
};

/** The search and the icons. It loads the SVGs each time the dialog opens. */
const IconLibrary: FC<{
  current: string;
  colour: string;
  onPick: (icon: LibraryIcon) => void;
}> = ({ current, colour, onPick }) => {
  const [query, setQuery] = useState("");
  const { data: svgs, error } = useIconLibrary();
  const matches = svgs
    ? findIcons(query).flatMap(({ name, label }) => {
        const svg = svgs.get(name);
        return svg ? [{ name, label, svg }] : [];
      })
    : [];

  return (
    <Stack spacing={1.5}>
      <TextField
        autoFocus
        fullWidth
        size="small"
        type="search"
        label="Search icons"
        placeholder="Search icons, e.g. award, people, money"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />
      <Typography role="status" variant="caption" sx={{ color: "text.secondary" }}>
        {svgs ? countText(matches.length, query.trim() !== "") : null}
      </Typography>
      {error ? <Alert severity="error">The icons could not be loaded.</Alert> : null}
      {!svgs && !error ? <LinearProgress /> : null}
      {svgs && matches.length === 0 ? (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          No icons match that search.
        </Typography>
      ) : null}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(88px, 1fr))",
          gap: 0.75,
        }}
      >
        {matches.map((icon) => {
          const chosen = icon.name === current;
          return (
            <ButtonBase
              key={icon.name}
              onClick={() => onPick(icon)}
              aria-pressed={chosen}
              sx={{
                height: 76,
                flexDirection: "column",
                gap: 0.75,
                borderRadius: "8px",
                border: chosen ? 2 : 1,
                borderColor: chosen ? colour : "divider",
                color: chosen ? colour : "text.secondary",
                bgcolor: chosen ? tint(colour) : undefined,
              }}
              data-analytics-id="program-icon-library-pick"
            >
              <ProgramIcon data={icon.svg} sx={{ fontSize: 24 }} />
              <Typography variant="caption" sx={{ lineHeight: "16px" }}>
                {icon.label}
              </Typography>
            </ButtonBase>
          );
        })}
      </Box>
    </Stack>
  );
};

/** The library of icons for a program, to search and pick one from. A pick closes it. */
export const IconLibraryDialog: FC<{
  open: boolean;
  /** The name of the icon that the program has now. */
  current: string;
  colour: string;
  onPick: (icon: LibraryIcon) => void;
  onClose: () => void;
}> = ({ open, current, colour, onPick, onClose }) => (
  <Dialog
    open={open}
    onClose={onClose}
    fullWidth
    maxWidth="sm"
    aria-labelledby="icon-library-title"
  >
    <DialogTitle id="icon-library-title" sx={{ pr: 7 }}>
      Choose an icon
    </DialogTitle>
    <IconButton
      aria-label="Close"
      onClick={onClose}
      sx={{ position: "absolute", right: 8, top: 8 }}
      data-analytics-id="program-icon-library-close"
    >
      <CloseIcon />
    </IconButton>
    <DialogContent dividers sx={{ minHeight: 400 }}>
      <IconLibrary current={current} colour={colour} onPick={onPick} />
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} data-analytics-id="program-icon-library-cancel">
        Cancel
      </Button>
    </DialogActions>
  </Dialog>
);
